import { StateService, Transition, TransitionService, UIRouterGlobals } from "@uirouter/angular";
import { Component, ElementRef, Input, OnDestroy, OnInit } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { EMPTY, Observable, Subject, combineLatest, debounceTime, distinctUntilChanged, filter, finalize, forkJoin, mergeMap, of, switchMap, take, tap } from "rxjs";
import { IIndicatorMap } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { ITeam, ITeamsIdMap } from "@gtmhub/teams";
import { CheckInMetadataEntry, CurrentUserService, getCurrentAccountId, getCurrentUserId } from "@gtmhub/users";
import { byEach, byTextField } from "@gtmhub/util/sorting-utils";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { AssigneesFacade } from "@webapp/assignees/services/assignees/assignees-facade.service";
import { AggregatedReaction, Comment, CommentsFacade, Reaction, ReactionsFacade } from "@webapp/comments";
import { ICollection } from "@webapp/core/core.models";
import { fromTransitionHook } from "@webapp/core/routing/rxjs";
import { IsNavigationExpandedCache } from "@webapp/navigation/services/is-navigation-expanded.cache";
import { CheckInDTO } from "@webapp/reflections/models/checkins-facade.models";
import { CheckIn, CheckInErrorMessages, CheckInNotes, CheckInSettings, CheckInTemplate } from "@webapp/reflections/models/checkins-models";
import { IReflectionContributor } from "@webapp/reflections/models/reflection-contributor-list.models";
import { ReflectionEdit, ReflectionPanel, ReflectionPublish } from "@webapp/reflections/models/reflection-panel.models";
import { CadenceChangeParams, ReflectionOverviewFilterName, ReflectionOverviewFilterType } from "@webapp/reflections/models/reflections.models";
import { REFLECTION_CADENCE_DURATION_IN_DAYS } from "@webapp/reflections/models/team-reflections.utils";
import { CheckInsDataService } from "@webapp/reflections/services/checkins-data.service";
import { CheckInsErrorService } from "@webapp/reflections/services/checkins-error.service";
import { CheckInsFacade } from "@webapp/reflections/services/checkins-facade.service";
import { mapCheckInResToPanel } from "@webapp/reflections/utils/map-check-in-res-to-panel";
import dayjs from "@webapp/shared/libs/dayjs";
import { OkrLinkClickEvent } from "@webapp/shared/rich-text-editor/events/okr-link.event";
import { TrackingEvents } from "@webapp/shared/utils/tracking";
import { WidthResizeObservable } from "@webapp/ui/core/core.utils";
import { UiToastService } from "@webapp/ui/toast/services/toast.service";
import {
  createCheckinPostRequestAnswersDTO,
  createCheckinPostRequestBody,
  createCheckinPutRequestAnswersDTO,
  createCheckinPutRequestBody,
  createCheckinRequestNotesDTO,
  createCheckinsRequestFilterByTeam,
  createCheckinsRequestFilterByTeamWithCadence,
  createdAtBelongsInCadance,
  extractUniqueTeamMemberIds,
  getLastDayOfCadence,
  getLastEditableMondayAsDate,
  getLastMondayAsDate,
  getPrevCadenceStartDate,
  startWithFieldValue,
  toEnrichedContributorListMember,
} from "../../utils";
import { checkInsMetadata } from "../checkins-new-label/checkins-new-label.component";

const COMMENTS_REQUEST_FIELDS: (keyof Comment)[] = ["id", "createdAt", "createdBy", "targetId"];

@UntilDestroy()
@Component({
  selector: "team-reflections",
  templateUrl: "./team-reflections.component.html",
  styleUrls: ["./team-reflections.component.less"],
})
export class TeamReflectionsComponent implements OnInit, OnDestroy {
  @Input() public includeCtaButton: boolean;
  // TODO: https://gtmhub.atlassian.net/browse/GVS-15530
  // Get teams and assignees from redux
  @Input() public teams: ITeamsIdMap;
  @Input() public assignees: Record<string, Assignee>;

  public checkInSettings: CheckInSettings;
  public checkInTemplate: CheckInTemplate;
  public currReflections: ReflectionPanel[];
  public prevReflections: ReflectionPanel[];
  public currentPage = 1;
  public pageSize = 15;
  public panelExpandedId: string;
  public members: IReflectionContributor[] = [];
  public currCadenceStartDate: string;
  public currCadenceEndDate: string;
  public prevCadenceStartDate: string;
  public prevCadenceEndDate: string;
  public latestAvailableDate: string;
  public isCompareModeEnabled: boolean;
  public filter: ReflectionOverviewFilterName;
  public team: ITeam;
  public filters: ReflectionOverviewFilterType = {
    published: { count: 0, selected: false },
    notPublished: { count: 0, selected: false },
    blockers: { count: 0, selected: false },
    requests: { count: 0, selected: false },
    wins: { count: 0, selected: false },
    kudos: { count: 0, selected: false },
    takeaways: { count: 0, selected: false },
  };
  public indicators: IIndicatorMap = {
    loadingSettingsAndTemplate: { progress: true },
    loadingIndicator: { progress: true },
  };
  public expandCollapseAll: Subject<boolean> = new Subject<boolean>();
  private currentUserId: string;
  private currentAccountId: string;
  public userBelongsToTeam: boolean;
  public isCompareModeAvailable: boolean;
  private initialCompareMode: boolean;
  public isLeftNavigationEnabled: boolean;
  private checkinsClientErrorsMap: CheckInErrorMessages = {
    "reflections can only be submitted or altered within the current or previous cadences": "checkins_cannot_submit_error",
  };

  constructor(
    private stateService: StateService,
    private routerGlobals: UIRouterGlobals,
    private transitionService: TransitionService,
    private checkInsFacade: CheckInsFacade,
    private checkInsDataService: CheckInsDataService,
    private assigneesFacade: AssigneesFacade,
    private analyticsService: AnalyticsService,
    private toastService: UiToastService,
    private currentUserService: CurrentUserService,
    private isNavExpanded: IsNavigationExpandedCache,
    private reactionsFacade: ReactionsFacade,
    private commentsFacade: CommentsFacade,
    private elementRef: ElementRef<HTMLElement>,
    private checkInsErrorService: CheckInsErrorService
  ) {
    this.assignees = this.assigneesFacade.getAssigneesIdMap();
    this.currentUserId = getCurrentUserId();
    this.currentAccountId = getCurrentAccountId();
  }

  public ngOnInit(): void {
    this.toggleIsCompareModeEnabledOnResize();
    this.isNavExpanded.get$().subscribe((value) => {
      this.isLeftNavigationEnabled = value;
    });

    this.setTeam();
    this.setTeamMembers();

    const reqFilter = createCheckinsRequestFilterByTeam(this.team.id);

    forkJoin([this.checkInsFacade.getCheckInSettings$(reqFilter), this.checkInsFacade.getCheckInTemplate$({ teamId: this.team.id })])
      .pipe(
        untilDestroyed(this),
        switchMap(([settings, template]) => {
          if (!settings || !template) return EMPTY;

          const { items: [checkInSettings = null] = [] } = settings;

          this.checkInTemplate = template;
          this.checkInSettings = checkInSettings;

          const memberIdFromQuery = this.routerGlobals.params["memberId"];
          this.panelExpandedId = this.getInitialExpandedMemberId(memberIdFromQuery);

          this.latestAvailableDate = getLastMondayAsDate();

          const cadenceStartFromQuery = this.routerGlobals.params["cadenceStart"];
          const isCadanceStartDateValid = this.getValidCadenceStartDate(cadenceStartFromQuery).valid;
          this.setCorrectCadanceStartDate(cadenceStartFromQuery);
          if (isCadanceStartDateValid) {
            return this.loadCheckIns(this.panelExpandedId).pipe(take(1));
          }

          return EMPTY;
        }),
        finalize(() => {
          this.indicators.loadingSettingsAndTemplate = undefined;
        })
      )
      .subscribe();

    fromTransitionHook(this.transitionService, "onSuccess", {})
      .pipe(untilDestroyed(this))
      .subscribe((transition) => {
        const memberId = transition.paramsChanged().memberId || this.routerGlobals.params["memberId"];
        const cadenceStart = transition.paramsChanged().cadenceStart;
        const isCadanceStartDateValid = this.getValidCadenceStartDate(cadenceStart).valid;
        if (memberId && memberId !== this.panelExpandedId) {
          this.panelExpandedId = this.getInitialExpandedMemberId(memberId);
          this.loadCheckInsAndReplaceEdited(this.loadCheckIns(this.panelExpandedId));
        }
        if (cadenceStart) {
          this.setCorrectCadanceStartDate(cadenceStart);
          if (isCadanceStartDateValid) {
            this.loadCheckInsAndReplaceEdited(this.loadCheckIns(this.panelExpandedId));
          }
        }
        if (this.routerGlobals.params["commentId"] && this.routerGlobals.params["targetId"]) {
          this.expandCommentsSectionByTargetId(this.routerGlobals.params["targetId"]);
        }
        this.trackOpenedDrawerViaLinkSelector(transition);
      });

    this.checkInTemplateOrSettingChange();
  }

  public ngOnDestroy(): void {
    this.checkInsErrorService.setClientErrorState(null);
    this.checkInsDataService.setCurrentCadanceReflectionEdit(null);
    this.checkInsDataService.setPreviousCadanceReflectionEdit(null);
  }

  public onSelectedFilterChange(event: ReflectionOverviewFilterType): void {
    this.currentPage = 1;
    this.filters = event;
    this.filter = Object.keys(this.filters).find((key) => this.filters[key].selected) as ReflectionOverviewFilterName;
    this.analyticsService.track("Filter Selected", {
      name: "checkin_overview",
      account_id: this.currentAccountId,
      team_id: this.team.id,
      user_id: this.currentUserId,
      category: this.filter,
      compare_weeks_enabled: this.isCompareModeEnabled,
    });
    this.resetNotificationsQueryParams();
  }

  public onCadenceChange(params: CadenceChangeParams): void {
    this.indicators.loadingIndicator = { progress: true };
    this.panelExpandedId = null;
    this.stateService.go(
      ".",
      { ...this.routerGlobals.params, cadenceStart: dayjs(params.currCadenceStartDate).format("YYYY-MM-DD") },
      { reload: false, notify: false, location: "replace" }
    );

    this.filter = undefined;
    this.resetFiltersSelected();
  }

  public selectCorrectCheckIns(reflectionEdited: ReflectionPanel): void {
    if (createdAtBelongsInCadance(reflectionEdited.createdAt, this.currCadenceStartDate, this.currCadenceEndDate)) {
      this.findAndReplaceCheckIn(reflectionEdited, this.currReflections);
    }
    if (createdAtBelongsInCadance(reflectionEdited.createdAt, this.prevCadenceStartDate, this.prevCadenceEndDate)) {
      this.findAndReplaceCheckIn(reflectionEdited, this.prevReflections);
    }
  }

  public findAndReplaceCheckIn(reflectionEdited: ReflectionPanel, reflections: ReflectionPanel[]): void {
    const reflectionToReplaceIndex = reflections.findIndex(
      (reflection) =>
        (reflection.createdAt === reflectionEdited.createdAt || reflection.id === reflectionEdited.id) && reflection.assignee.id === reflectionEdited.assignee.id
    );
    if (reflectionToReplaceIndex !== -1) {
      reflections.splice(reflectionToReplaceIndex, 1, reflectionEdited);
    }
  }

  public toggleCompareMode(compareMode: boolean): void {
    this.isCompareModeEnabled = compareMode;
    this.updateFiltersCounter();
    this.analyticsService.track(TrackingEvents.ToggleSet, {
      name: "checkins_compare_weeks",
      account_id: this.currentAccountId,
      team_id: this.team.id,
      user_id: this.currentUserId,
      state: compareMode,
    });
    window.setTimeoutOutsideAngular(() => {
      const panelHeader = this.elementRef.nativeElement.querySelector<HTMLElement>(".ant-collapse-header");

      if (!panelHeader) {
        return;
      }

      panelHeader.scrollIntoView({ block: "nearest" });
      panelHeader.focus();
    });
  }

  public onReflectionPublish(reflectionEdited: ReflectionPublish): void {
    let prevReflectionSnapshot: ReflectionPanel;
    let currReflectionSnapshot: ReflectionPanel;

    if (this.isCompareModeEnabled) {
      prevReflectionSnapshot = this.prevReflections[0];
      currReflectionSnapshot = this.currReflections[0];
    }

    if (this.createdAtBelongsInCurrentCadance(reflectionEdited.checkin.createdAt)) {
      this.checkInsDataService.setCurrentCadanceReflectionEdit(null);
    } else {
      this.checkInsDataService.setPreviousCadanceReflectionEdit(null);
    }

    const notesDTO = createCheckinRequestNotesDTO(reflectionEdited.checkin?.notes);

    if (reflectionEdited.checkin.id) {
      this.handleModifyCheckin(reflectionEdited, notesDTO, prevReflectionSnapshot, currReflectionSnapshot);
    } else {
      this.handleCreateCheckin(reflectionEdited, notesDTO, prevReflectionSnapshot, currReflectionSnapshot);
    }
  }

  private handleModifyCheckin(
    reflectionEdited: ReflectionPublish,
    notesDTO: CheckInNotes,
    prevReflectionSnapshot: ReflectionPanel,
    currReflectionSnapshot: ReflectionPanel
  ): void {
    const answersDTO = createCheckinPutRequestAnswersDTO(reflectionEdited.checkin.questions);

    const reqBody = createCheckinPutRequestBody({
      mood: reflectionEdited.checkin.mood,
      answers: answersDTO,
      notesV2: notesDTO,
    });

    const reqParams = { reflectionId: reflectionEdited.checkin.id };

    this.handleCheckInRequest(reqBody, reqParams, prevReflectionSnapshot, currReflectionSnapshot, "modify_checkin", "checkin_update_success_message", reflectionEdited);
  }

  private handleCreateCheckin(
    reflectionEdited: ReflectionPublish,
    notesDTO: CheckInNotes,
    prevReflectionSnapshot: ReflectionPanel,
    currReflectionSnapshot: ReflectionPanel
  ): void {
    const answersDTO = createCheckinPostRequestAnswersDTO(reflectionEdited.checkin.questions);

    const settingsDTO = {
      moodsEnabled: this.checkInTemplate.moodsEnabled,
      notesEnabled: this.checkInTemplate.notesEnabled,
    };
    const cadenceStartDate = reflectionEdited.type === "current" ? this.currCadenceStartDate : this.prevCadenceStartDate;

    const reqBody = createCheckinPostRequestBody({
      teamId: this.team.id,
      templateId: this.checkInTemplate.id,
      mood: reflectionEdited.checkin.mood,
      answers: answersDTO,
      notesV2: notesDTO,
      settings: settingsDTO,
      cadenceDate: cadenceStartDate,
    });

    this.handleCheckInRequest(reqBody, null, prevReflectionSnapshot, currReflectionSnapshot, "create_checkin", "checkin_create_success_message", reflectionEdited);
  }

  private handleCheckInRequest(
    reqBody: Partial<CheckIn>,
    reqParams: { reflectionId: string },
    prevReflectionSnapshot: ReflectionPanel,
    currReflectionSnapshot: ReflectionPanel,
    eventName: string,
    successMessage: string,
    reflectionEdited: ReflectionPublish
  ): void {
    this.analyticsService.track("Button Clicked", {
      name: eventName,
      account_id: this.currentAccountId,
      team_id: this.team.id,
      user_id: this.currentUserId,
      compare_weeks_enabled: this.isCompareModeEnabled,
    });

    const checkInObservable = reqParams ? this.checkInsFacade.putCheckIn$(reqBody, reqParams) : this.checkInsFacade.postCheckIn$(reqBody);

    checkInObservable.pipe(switchMap(() => this.loadCheckIns(getCurrentUserId()).pipe(take(1)))).subscribe({
      next: () => {
        this.enableEditModeByCheckinTypeInCompareMode(reflectionEdited, prevReflectionSnapshot, currReflectionSnapshot);
        this.createSuccessNotification(successMessage);

        if (!reqParams) {
          this.updateNewLabelStorageData();
          this.checkInsErrorService.setClientErrorState(null);
        }
      },
      error: (e) => {
        this.handleCheckInError(e, { isEdit: !!reqParams });
      },
    });
  }

  private handleCheckInError(e, { isEdit }: { isEdit: boolean }): void {
    if (e.status === 403 && this.checkinsClientErrorsMap[e.error]) {
      this.checkInsErrorService.setClientErrorState({
        message: isEdit ? "unable_to_edit_checkin_error" : "unable_to_publish_checkin_error",
        description: this.checkinsClientErrorsMap[e.error],
      });
    } else {
      this.checkInsErrorService.setClientErrorState({ message: "server_error_message", description: "server_error_suggestion_text" });
    }
  }

  public onPageChange(page: number): void {
    this.currentPage = page;
  }

  public calculatePageNumber(expandedId: string, currReflections: ReflectionPanel[]): void {
    const index = currReflections
      .map((reflection) => {
        return reflection.assignee.id;
      })
      .indexOf(expandedId);

    const newCurrentPage = Math.ceil((index + 1) / this.pageSize);
    this.currentPage = newCurrentPage;
  }

  public onReflectionEdit(reflectionEdited: ReflectionEdit): void {
    if (this.createdAtBelongsInCurrentCadance(reflectionEdited.checkin.createdAt)) {
      if (!reflectionEdited.editCancel) {
        this.checkInsDataService.setCurrentCadanceReflectionEdit(reflectionEdited.checkin);
      } else {
        this.checkInsDataService.setCurrentCadanceReflectionEdit(null);
      }
    }

    if (this.createdAtBelongsInPreviousCadance(reflectionEdited.checkin.createdAt)) {
      if (!reflectionEdited.editCancel) {
        this.checkInsDataService.setPreviousCadanceReflectionEdit(reflectionEdited.checkin);
      } else {
        this.checkInsDataService.setPreviousCadanceReflectionEdit(null);
      }
    }
  }

  public toggleExpandState(state: boolean): void {
    this.expandCollapseAll.next(state);
  }

  public okrLinkClicked(event: OkrLinkClickEvent): void {
    this.stateService.go(event.stateParams.path, event.stateParams.params);
  }

  private updateNewLabelStorageData(): void {
    const newLabelFromStorage = this.currentUserService.getUserSetting<CheckInMetadataEntry>(checkInsMetadata);
    if (newLabelFromStorage.newLabel) {
      this.currentUserService.setUserSetting({ [checkInsMetadata]: { newLabel: { ...newLabelFromStorage.newLabel, userHasPosted: true } } });
    }
  }

  private loadCheckInsAndReplaceEdited(request: Observable<{ currReflectionsRes: ICollection<CheckInDTO>; prevReflectionsRes: ICollection<CheckInDTO> }>): void {
    const lastEditableMondayAsDate = getLastEditableMondayAsDate();
    const shouldReplaceWithEditedCheckIn = dayjs(this.currCadenceEndDate).isAfter(lastEditableMondayAsDate);
    if (!shouldReplaceWithEditedCheckIn) {
      request.pipe(take(1)).subscribe();
    } else {
      request
        .pipe(
          switchMap(() => {
            return combineLatest([this.checkInsDataService.currentCadanceReflectionEdit$(), this.checkInsDataService.previousCadanceReflectionEdit$()]);
          }),
          take(1)
        )
        .subscribe(([currReflectionEdited, prevReflectionEdited]) => {
          if (currReflectionEdited) {
            this.selectCorrectCheckIns(currReflectionEdited);
          }
          if (prevReflectionEdited) {
            this.selectCorrectCheckIns(prevReflectionEdited);
          }
        });
    }
  }

  private calculateCurrAndPrevCadance(currCadenceStartDate: string): void {
    const prevCadenceStartDate = getPrevCadenceStartDate(currCadenceStartDate, REFLECTION_CADENCE_DURATION_IN_DAYS);
    const currCadenceEndDate = getLastDayOfCadence(currCadenceStartDate, REFLECTION_CADENCE_DURATION_IN_DAYS);
    const prevCadenceEndDate = getLastDayOfCadence(prevCadenceStartDate, REFLECTION_CADENCE_DURATION_IN_DAYS);

    this.setInitialCadencesStartAndEndDate({
      currCadenceStartDate,
      currCadenceEndDate,
      prevCadenceStartDate,
      prevCadenceEndDate,
    });
  }

  public loadCheckIns(expandedId?: string): Observable<{
    currReflectionsRes: ICollection<CheckInDTO>;
    prevReflectionsRes: ICollection<CheckInDTO>;
  }> {
    return forkJoin({
      currReflectionsRes: this.loadCurrReflections(),
      prevReflectionsRes: this.loadPrevReflections(),
    }).pipe(
      tap(({ currReflectionsRes, prevReflectionsRes }) => {
        const checkInTeam = structuredClone(this.team);
        this.userBelongsToTeam = [...(this.team.members || []), this.team.manager]?.includes(getCurrentUserId());
        this.currReflections = mapCheckInResToPanel(currReflectionsRes.items, checkInTeam, this.checkInTemplate, this.currCadenceStartDate, expandedId);
        this.prevReflections = mapCheckInResToPanel(prevReflectionsRes.items, checkInTeam, this.checkInTemplate, this.prevCadenceStartDate, expandedId);
        this.indicators.loadingIndicator = undefined;

        const published = this.routerGlobals.params["published"];

        if (expandedId && published) {
          const publishedReflections = this.currReflections.filter((reflection) => !reflection.noReflection);
          this.filters.published.selected = true;
          this.onSelectedFilterChange({ ...this.filters });

          if (!this.routerGlobals.params["commentId"] && !this.routerGlobals.params["targetId"]) {
            this.calculatePageNumber(expandedId, publishedReflections);
          }
        }

        if (!expandedId && published) {
          this.filters.published.selected = true;
          this.onSelectedFilterChange({ ...this.filters });
        }

        if (expandedId && !published) {
          this.calculatePageNumber(expandedId, this.currReflections);
        }

        this.updateFiltersCounter();
      }),
      switchMap(({ currReflectionsRes, prevReflectionsRes }) => {
        const targetIds = this.getTargetIds();
        let reactionsReq = of([]);
        let commentsReq = of([]);

        if (targetIds) {
          const filter = { fields: COMMENTS_REQUEST_FIELDS };
          const queryParams = {
            targetIds,
            sort: "createdAt",
          };

          reactionsReq = this.reactionsFacade.getReactions$({ targetIds });
          commentsReq = this.commentsFacade.getComments$(filter, queryParams);
        }

        forkJoin({ reactionsReq, commentsReq })
          .pipe(take(1))
          .subscribe((data) => {
            this.enrichQuestionsAndNotesWithReactionsAndComments(this.currReflections, data.reactionsReq, data.commentsReq);
            this.enrichQuestionsAndNotesWithReactionsAndComments(this.prevReflections, data.reactionsReq, data.commentsReq);
          });

        if (this.routerGlobals.params["commentId"] && this.routerGlobals.params["targetId"]) {
          this.expandCommentsSectionByTargetId(this.routerGlobals.params["targetId"]);
        }

        return of({ currReflectionsRes, prevReflectionsRes });
      })
    );
  }

  private toggleIsCompareModeEnabledOnResize(): void {
    new WidthResizeObservable(document.body).pipe(debounceTime(200), distinctUntilChanged(), untilDestroyed(this)).subscribe((width) => {
      if (width <= 768 && this.isCompareModeAvailable) {
        this.initialCompareMode = this.isCompareModeEnabled;
        this.isCompareModeAvailable = false;
        if (this.initialCompareMode) {
          this.toggleCompareMode(false);
        }
      } else if (width > 768 && !this.isCompareModeAvailable) {
        this.isCompareModeAvailable = true;
        if (this.initialCompareMode) {
          this.toggleCompareMode(true);
        }
      }
    });
  }

  private updateFiltersCounter(): void {
    this.resetFiltersCounter();
    this.calculateFiltersCounter(this.currReflections);

    if (this.isCompareModeEnabled && this.prevReflections) {
      this.calculateFiltersCounter(this.prevReflections);
    }
  }

  private resetFiltersCounter(): void {
    this.filters = Object.keys(this.filters).reduce((filterMap, filterType) => {
      filterMap[filterType] = { ...this.filters[filterType], count: 0 };
      return filterMap;
    }, {});
  }

  private resetFiltersSelected(): void {
    this.filters = Object.keys(this.filters).reduce((filterMap, filterType) => {
      filterMap[filterType] = { ...this.filters[filterType], selected: false };
      return filterMap;
    }, {});
  }

  private calculateFiltersCounter(checkins: ReflectionPanel[]): void {
    const updatedFilters = this.filters;
    checkins?.forEach((checkin) => {
      if (checkin.noReflection) {
        updatedFilters.notPublished.count += 1;
      } else {
        updatedFilters.published.count += 1;
      }

      checkin?.notes?.forEach((note) => {
        if (note.text || note?.gif?.id) {
          updatedFilters[note.type].count += 1;
        }
      });
    });
    this.filters = updatedFilters;
  }

  private setTeam(): void {
    const currentTeamId = this.routerGlobals.params["teamId"];
    this.team = this.teams[currentTeamId];
  }

  private setTeamMembers(): void {
    const currentUserId = getCurrentUserId();
    const memberIds = extractUniqueTeamMemberIds(this.team);

    this.members = memberIds
      .map((userId) => toEnrichedContributorListMember(this.assignees[userId]))
      .sort(byEach(startWithFieldValue("id", currentUserId), byTextField("name")));
  }

  private getInitialExpandedMemberId(memberIdFromQuery: string): string {
    const isMemberIdValid = this.members.some((member) => member.id && member.id === memberIdFromQuery);
    return isMemberIdValid ? memberIdFromQuery : null;
  }

  private setCorrectCadanceStartDate(cadenceStartFromQuery: string | undefined): void {
    const currCadenceStartDate = this.getValidCadenceStartDate(cadenceStartFromQuery);
    if (!currCadenceStartDate.valid) {
      this.onCadenceChange({
        currCadenceStartDate: currCadenceStartDate.date,
      });
    } else {
      this.calculateCurrAndPrevCadance(currCadenceStartDate.date);
    }
  }

  private getValidCadenceStartDate(cadenceStartFromQuery: string | undefined): { valid: boolean; date: string } {
    const isValidDate = cadenceStartFromQuery && dayjs(cadenceStartFromQuery).isValid();
    const isAfterLatestCadanceEnd = dayjs(cadenceStartFromQuery).isAfter(getLastDayOfCadence(getLastMondayAsDate(), REFLECTION_CADENCE_DURATION_IN_DAYS));
    if (!isValidDate || isAfterLatestCadanceEnd) {
      return { valid: false, date: getLastMondayAsDate() };
    }

    const isValidCadenceStart = dayjs(cadenceStartFromQuery).day() === 1;
    if (!isValidCadenceStart) {
      return { valid: false, date: dayjs(cadenceStartFromQuery).startOf("isoWeek").toISOString() };
    }

    return { valid: true, date: dayjs(cadenceStartFromQuery).toISOString() };
  }

  private setInitialCadencesStartAndEndDate({ currCadenceStartDate, currCadenceEndDate, prevCadenceStartDate, prevCadenceEndDate }: CadenceChangeParams): void {
    this.currCadenceStartDate = currCadenceStartDate;
    this.currCadenceEndDate = currCadenceEndDate;
    this.prevCadenceStartDate = prevCadenceStartDate;
    this.prevCadenceEndDate = prevCadenceEndDate;
  }

  private loadCurrReflections(): Observable<ICollection<CheckInDTO>> {
    const reqFilter = createCheckinsRequestFilterByTeamWithCadence(this.team.id, this.currCadenceStartDate, this.currCadenceEndDate);
    return this.checkInsFacade.getCheckIns$(reqFilter);
  }

  private loadPrevReflections(): Observable<ICollection<CheckInDTO>> {
    const reqFilter = createCheckinsRequestFilterByTeamWithCadence(this.team.id, this.prevCadenceStartDate, this.prevCadenceEndDate);
    return this.checkInsFacade.getCheckIns$(reqFilter);
  }

  private createdAtBelongsInCurrentCadance(createdAt: string): boolean {
    return createdAtBelongsInCadance(createdAt, getLastMondayAsDate(), getLastDayOfCadence(getLastMondayAsDate(), REFLECTION_CADENCE_DURATION_IN_DAYS));
  }

  private createdAtBelongsInPreviousCadance(createdAt: string): boolean {
    return createdAtBelongsInCadance(createdAt, getLastEditableMondayAsDate(), getLastDayOfCadence(getLastEditableMondayAsDate(), REFLECTION_CADENCE_DURATION_IN_DAYS));
  }

  private createSuccessNotification(messageKey: string): void {
    this.toastService.success(localize(messageKey), {
      uiCloseable: true,
      uiShouldTimeout: true,
      uiDuration: 2000,
      uiOffset: 24,
    });
  }

  private enableEditModeByCheckinTypeInCompareMode(
    reflectionEdited: ReflectionPublish,
    prevReflectionSnapshot: ReflectionPanel,
    currReflectionSnapshot: ReflectionPanel
  ): void {
    if (this.isCompareModeEnabled) {
      if (reflectionEdited.type === "current" && prevReflectionSnapshot.isEditing) {
        this.prevReflections[0] = prevReflectionSnapshot;
      } else if (currReflectionSnapshot.isEditing) {
        this.currReflections[0] = currReflectionSnapshot;
      }
    }
  }

  private checkInTemplateOrSettingChange(): void {
    this.checkInsDataService
      .getCheckInTemplateSettingsData$()
      .pipe(
        filter((data) => !!data),
        untilDestroyed(this),
        mergeMap((data) => {
          if (data.template) {
            this.checkInTemplate = data.template;
          }

          if (data.settings) {
            this.checkInSettings = data.settings;
          }
          return this.loadCheckIns().pipe(take(1));
        })
      )
      .subscribe();
  }

  private enrichQuestionsAndNotesWithReactionsAndComments(checkins: ReflectionPanel[], reactions: Reaction[], comments: Comment[]): void {
    checkins.forEach((checkin) => {
      checkin.questions.forEach((question) => {
        question.reactions = (reactions || []).filter((reaction) => reaction.targetId === question?.answerId) as AggregatedReaction[];

        const commentsAuthorsMap = {};

        question.comments = comments.reduce((comments, comment) => {
          if (comment.targetId === question?.answerId) {
            comments.push(comment);
            commentsAuthorsMap[comment.createdBy] = this.assignees[comment.createdBy];
          }
          return comments;
        }, []);
        question.commentsAssignees = Object.values(commentsAuthorsMap);
      });
      checkin.notes.forEach((note) => {
        note.reactions = (reactions || []).filter((reaction) => reaction.targetId === note.id) as AggregatedReaction[];

        const commentsAuthorsMap = {};

        note.comments = comments.reduce((comments, comment) => {
          if (comment.targetId === note.id) {
            comments.push(comment);
            commentsAuthorsMap[comment.createdBy] = this.assignees[comment.createdBy];
          }
          return comments;
        }, []);
        note.commentsAssignees = Object.values(commentsAuthorsMap);
      });
    });
  }

  private exportQuestionsAndNotesIDsFromCheckins(checkins: ReflectionPanel[]): string[] {
    const ids = [];

    checkins.forEach((checkin) => {
      if (!checkin.noReflection) {
        checkin.questions.forEach((question) => {
          if (question?.answerId) {
            ids.push(question.answerId);
          }
        });
        checkin.notes.forEach((note) => {
          if (note.id) {
            ids.push(note.id);
          }
        });
      }
    });

    return ids;
  }

  private getTargetIds(): string {
    let targetIds = [];
    targetIds = this.exportQuestionsAndNotesIDsFromCheckins(this.currReflections);

    if (this.prevReflections?.length) {
      targetIds = [...targetIds, ...this.exportQuestionsAndNotesIDsFromCheckins(this.prevReflections)];
    }

    return targetIds.join(",");
  }

  private expandCommentsSectionByTargetId(targetId: string): void {
    for (const checkin of this.currReflections) {
      const questionMatch = checkin.questions.find((question) => question?.answerId === targetId);
      if (questionMatch) {
        const questions = checkin.questions;
        questions[questions.indexOf(questionMatch)].isCommentsEnabled = true;
        return;
      }
      const noteMatch = checkin.notes.find((note) => note.id === targetId);
      if (noteMatch) {
        const notes = checkin.notes;
        notes[notes.indexOf(noteMatch)].isCommentsEnabled = true;
        return;
      }
    }
  }

  private resetNotificationsQueryParams(): void {
    const { commentId, memberId, targetId, published } = this.routerGlobals.params;
    if (commentId || memberId || targetId || published) {
      this.stateService.go(".", { commentId: "", memberId: "", targetId: "", published: "" }, { reload: false, notify: false, location: "replace" });
    }
  }

  private trackOpenedDrawerViaLinkSelector(transition: Transition): void {
    const {
      name,
      data: { trackingPage },
    } = transition.to();
    if (["gtmhub.home.dashboard.team._goalSidebar.goal", "gtmhub.home.dashboard.team._metric.metric", "gtmhub.home.dashboard.team.details"].includes(name)) {
      this.analyticsService.track("Loaded a Page", {
        name: trackingPage,
        source: "check_ins",
      });
    }
  }
}
